import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { rbacUrl } from '../rbac/api';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Radio,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

export default function RbacUserOverrides() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [roles, setRoles] = useState([]);
  const [userRoleIds, setUserRoleIds] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    fetch(
      `${config.API_BASE_URL}/Home/GetAllUsers/${encodeURIComponent(company?.id)}`,
      {
        headers: authHeaders(),
      },
    )
      .then((r) => r.json())
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));

    fetch(rbacUrl('rbac/roles'), { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setRoles)
      .catch(() => setRoles([]));
  }, [company?.id]);

  const normalizeOverrideRows = (rows) =>
    (Array.isArray(rows) ? rows : []).map((row) => ({
      permissionId: row.permissionId ?? row.PermissionId,
      code: row.code ?? row.Code,
      name: row.name ?? row.Name,
      moduleKey: row.moduleKey ?? row.ModuleKey,
      overrideType: row.overrideType ?? row.OverrideType ?? null,
    }));

  const loadUser = useCallback(async (uid) => {
    if (!uid) return;
    setLoading(true);
    setLoadError('');
    try {
      const [oRes, rRes] = await Promise.all([
        fetch(rbacUrl(`rbac/users/${uid}/overrides`), {
          headers: authHeaders(),
        }),
        fetch(rbacUrl(`rbac/users/${uid}/roles`), {
          headers: authHeaders(),
        }),
      ]);
      if (oRes.ok) {
        setOverrides(normalizeOverrideRows(await oRes.json()));
      } else {
        setOverrides([]);
        setLoadError(
          oRes.status === 403
            ? 'Access denied. Log in as Super Admin or a user with RBAC management rights.'
            : `Could not load permissions (${oRes.status}). Rebuild API and ensure RBAC seed SQL was run.`,
        );
      }
      if (rRes.ok) setUserRoleIds(await rRes.json());
    } catch {
      setLoadError('Network error while loading permissions.');
      setOverrides([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userId) loadUser(userId);
  }, [userId, loadUser]);

  const setOverride = (permissionId, type) => {
    setOverrides((prev) =>
      prev.map((row) =>
        row.permissionId === permissionId
          ? { ...row, overrideType: type }
          : row,
      ),
    );
  };

  // Bulk-apply one overrideType (1=Allow, 2=Deny, null=Inherit) to every row.
  const setAllOverrides = (type) => {
    setOverrides((prev) => prev.map((row) => ({ ...row, overrideType: type })));
  };

  // Bulk-apply one overrideType to every row inside a single module group.
  const setModuleOverrides = (moduleKey, type) => {
    setOverrides((prev) =>
      prev.map((row) =>
        (row.moduleKey || 'Other') === moduleKey
          ? { ...row, overrideType: type }
          : row,
      ),
    );
  };

  const saveRoles = async () => {
    if (!userId) return;
    await fetch(rbacUrl(`rbac/users/${userId}/roles`), {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ roleIds: userRoleIds }),
    });
    toast.success('Roles saved. User must log in again for full effect.');
  };

  const saveOverrides = async () => {
    if (!userId) return;
    const active = overrides.filter(
      (o) => o.overrideType === 1 || o.overrideType === 2,
    );
    const allowCount = active.filter((o) => o.overrideType === 1).length;
    const denyCount = active.filter((o) => o.overrideType === 2).length;
    const ok = window.confirm(
      `Save ${allowCount} Allow and ${denyCount} Deny override(s)?\n\n` +
        'Tip: For Reception staff, assign the Reception role above instead of ' +
        'Allow on many rows. Overrides are only for exceptions.',
    );
    if (!ok) return;

    const payload = {
      overrides: active.map((o) => ({
        permissionId: o.permissionId,
        overrideType: o.overrideType,
      })),
    };
    const res = await fetch(rbacUrl(`rbac/users/${userId}/overrides`), {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      toast.success('Overrides saved. That user must log out and log in again.');
      const currentId = sessionStorage.getItem('userId');
      if (currentId === String(userId)) {
        sessionStorage.removeItem('permissionCodes');
        sessionStorage.removeItem('rbacEnforced');
      }
      loadUser(userId);
    } else toast.error('Save failed');
  };

  const grouped = overrides.reduce((acc, row) => {
    const k = row.moduleKey || 'Other';
    if (!acc[k]) acc[k] = [];
    acc[k].push(row);
    return acc;
  }, {});

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant='h5' fontWeight={700} sx={{ mb: 2 }}>
        User Roles & Permission Overrides
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }} size='small'>
        <InputLabel>Select user</InputLabel>
        <Select
          label='Select user'
          value={userId}
          onChange={(e) => setUserId(e.target.value)}>
          {users.map((u) => (
            <MenuItem key={u.id} value={String(u.id)}>
              {u.name || u.username} (#{u.id})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {userId && (
        <>
          <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Assign roles
            </Typography>
            <FormControl fullWidth size='small'>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                label='Roles'
                value={userRoleIds}
                onChange={(e) => setUserRoleIds(e.target.value)}
                renderValue={(sel) =>
                  roles
                    .filter((r) => sel.includes(r.id))
                    .map((r) => r.name)
                    .join(', ')
                }>
                {roles.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button sx={{ mt: 1 }} variant='outlined' onClick={saveRoles}>
              Save roles
            </Button>
          </Paper>

          {loadError && (
            <Typography color='error' sx={{ mb: 2 }}>
              {loadError}
            </Typography>
          )}

          {loading ? (
            <CircularProgress />
          ) : overrides.length === 0 ? (
            <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#f8fafc' }}>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                No permissions found in the database. Run the RBAC seed script
                on SQL Server, then refresh this page:
              </Typography>
              <Typography
                variant='caption'
                component='div'
                sx={{ fontFamily: 'monospace' }}>
                NatureFarm_HotelAPI/Scripts/Enterprise_Rbac_Full.sql
              </Typography>
              <Typography variant='body2' sx={{ mt: 2 }}>
                To set permissions for most users, use{' '}
                <strong>Roles &amp; Permissions</strong> (edit the Reception
                role) instead of this screen.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Permission</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 700 }}>
                      Allow
                      <Box>
                        <Button
                          size='small'
                          onClick={() => setAllOverrides(1)}
                          sx={{
                            textTransform: 'none',
                            fontSize: 11,
                            minWidth: 0,
                            p: 0.3,
                          }}>
                          Select all
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell align='center' sx={{ fontWeight: 700 }}>
                      Deny
                      <Box>
                        <Button
                          size='small'
                          color='error'
                          onClick={() => setAllOverrides(2)}
                          sx={{
                            textTransform: 'none',
                            fontSize: 11,
                            minWidth: 0,
                            p: 0.3,
                          }}>
                          Select all
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell align='center' sx={{ fontWeight: 700 }}>
                      Inherit (role)
                      <Box>
                        <Button
                          size='small'
                          color='inherit'
                          onClick={() => setAllOverrides(null)}
                          sx={{
                            textTransform: 'none',
                            fontSize: 11,
                            minWidth: 0,
                            p: 0.3,
                          }}>
                          Select all
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(grouped).map(([mod, rows]) => (
                    <React.Fragment key={mod}>
                      <TableRow>
                        <TableCell
                          sx={{ bgcolor: '#f1f5f9', fontWeight: 700 }}>
                          {mod}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ bgcolor: '#f1f5f9' }}>
                          <Button
                            size='small'
                            onClick={() => setModuleOverrides(mod, 1)}
                            sx={{
                              textTransform: 'none',
                              fontSize: 11,
                              minWidth: 0,
                              p: 0.3,
                            }}>
                            All
                          </Button>
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ bgcolor: '#f1f5f9' }}>
                          <Button
                            size='small'
                            color='error'
                            onClick={() => setModuleOverrides(mod, 2)}
                            sx={{
                              textTransform: 'none',
                              fontSize: 11,
                              minWidth: 0,
                              p: 0.3,
                            }}>
                            All
                          </Button>
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{ bgcolor: '#f1f5f9' }}>
                          <Button
                            size='small'
                            color='inherit'
                            onClick={() => setModuleOverrides(mod, null)}
                            sx={{
                              textTransform: 'none',
                              fontSize: 11,
                              minWidth: 0,
                              p: 0.3,
                            }}>
                            All
                          </Button>
                        </TableCell>
                      </TableRow>
                      {rows.map((row) => (
                        <TableRow key={row.permissionId}>
                          <TableCell>
                            <Typography variant='body2'>{row.name}</Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'>
                              {row.code}
                            </Typography>
                          </TableCell>
                          <TableCell align='center'>
                            <Radio
                              checked={row.overrideType === 1}
                              onChange={() => setOverride(row.permissionId, 1)}
                            />
                          </TableCell>
                          <TableCell align='center'>
                            <Radio
                              checked={row.overrideType === 2}
                              onChange={() => setOverride(row.permissionId, 2)}
                            />
                          </TableCell>
                          <TableCell align='center'>
                            <Radio
                              checked={row.overrideType == null}
                              onChange={() =>
                                setOverride(row.permissionId, null)
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography
            variant='caption'
            color='text.secondary'
            display='block'
            sx={{ mt: 1 }}>
            Tip: Assign a role above first. Use Allow/Deny here only for
            exceptions (e.g. deny one action for this user while keeping the
            role).
          </Typography>

          <Button
            variant='contained'
            startIcon={<SaveIcon />}
            sx={{ mt: 2 }}
            onClick={saveOverrides}
            disabled={!userId || loading}>
            Save overrides
          </Button>
        </>
      )}
    </Box>
  );
}
